import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable, merge } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { DrawerService } from './drawer.service';

/**
 * Knows whether the user is mid-edit in the side drawer or a dialog.
 *
 * Persistent bottom-right banners (the new-release toast, the System Emails
 * warning) overlap the drawer's Cancel/Update buttons, so firing one during an
 * editing flow leaves the user unable to finish it. They hold back until the
 * user is clear of both surfaces.
 */
@Injectable({
    providedIn: 'root',
})
export class EditingFlowService {
    constructor(
        private dialog: MatDialog,
        private drawerService: DrawerService,
    ) {}

    isOpen(): boolean {
        // Left drawer (mobile nav menu) is deliberately excluded — it isn't an
        // editing flow and doesn't sit over the toast.
        return (
            this.dialog.openDialogs.length > 0 ||
            !!this.drawerService.drawer?.opened
        );
    }

    /**
     * Emits once, the first time the user is clear of BOTH surfaces. Re-checks
     * on every close event because a dialog can be opened on top of the drawer —
     * closing one doesn't mean the other is gone.
     */
    whenClosed(): Observable<void> {
        const drawerClosed$ = this.drawerService.drawer
            ? this.drawerService.drawer.openedChange.pipe(
                  filter((opened) => !opened),
              )
            : EMPTY;

        return merge(this.dialog.afterAllClosed, drawerClosed$).pipe(
            filter(() => !this.isOpen()),
            take(1),
            map(() => undefined),
        );
    }
}
